import { Category, CategoryService, ProductService } from '@core/services/steelfab';
import { Product } from '@core/services/steelfab/product.service';
import { Inject } from '@rxdi/core';
import { Component, css, html, LitElement, state } from '@rxdi/lit-html';
import { Router } from '@rxdi/router';
import { SteelFabTheme } from '@shared/styles/theme';

import { ProductsListComponent } from '../../products/products-list/products-list.component';

/**
 * @customElement featured-products-component
 */
@Component({
  selector: 'featured-products-component',
  styles: [
    css`
      ${SteelFabTheme}

      :host {
        display: block;
      }

      .section {
        padding: 90px 0;
        background: var(--sf-bg-secondary);
      }

      .section-badge {
        display: inline-block;
        background: var(--sf-bg-primary);
        color: var(--sf-navy);
        border: 1px solid var(--sf-border-light);
        font-size: var(--sf-font-size-sm);
        font-weight: 600;
        padding: 6px 16px;
        border-radius: var(--sf-radius-pill);
        margin-bottom: 1rem;
        letter-spacing: 0.05em;
        text-transform: uppercase;
      }

      .section-header {
        text-align: center;
        margin-bottom: 3rem;
      }

      .section-title {
        font-size: var(--sf-font-size-4xl);
        font-weight: 700;
        color: var(--sf-text-primary);
        margin: 0 0 1rem;
        font-family: var(--sf-font-sans);
        letter-spacing: -0.02em;
      }

      .section-subtitle {
        font-size: var(--sf-font-size-lg);
        color: var(--sf-text-secondary);
        max-width: 540px;
        margin: 0 auto;
        line-height: 1.6;
      }

      .grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        gap: 2rem;
      }

      .product-card {
        background: var(--sf-bg-primary);
        border: 1px solid var(--sf-border-light);
        border-radius: var(--sf-radius-md);
        overflow: hidden;
        cursor: pointer;
        transition: all 0.3s ease;
        box-shadow: var(--sf-shadow-sm);
        display: flex;
        flex-direction: column;
      }

      .product-card:hover {
        transform: translateY(-3px);
        box-shadow: var(--sf-shadow-md);
        border-color: var(--sf-steel);
      }

      .product-image {
        width: 100%;
        height: 220px;
        background: linear-gradient(145deg, var(--sf-bg-secondary) 0%, var(--sf-bg-tertiary) 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 5rem;
        position: relative;
        border-bottom: 1px solid var(--sf-border-light);
        transition: background 0.3s ease;
      }

      .product-image img {
        width: 80%;
      }

      .product-card:hover .product-image {
        background: linear-gradient(145deg, var(--sf-bg-tertiary) 0%, #dce8f5 100%);
      }

      .product-badge {
        position: absolute;
        top: 12px;
        left: 12px;
        background: var(--sf-gradient-accent);
        color: white;
        font-size: var(--sf-font-size-xs);
        font-weight: 700;
        padding: 4px 10px;
        border-radius: var(--sf-radius-pill);
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }

      .product-body {
        padding: 1.5rem;
        flex: 1;
        display: flex;
        flex-direction: column;
      }

      .product-category {
        font-size: var(--sf-font-size-xs);
        font-weight: 600;
        color: var(--sf-steel);
        text-transform: uppercase;
        letter-spacing: 0.08em;
        margin-bottom: 0.5rem;
      }

      .product-name {
        font-size: var(--sf-font-size-xl);
        font-weight: 700;
        color: var(--sf-text-primary);
        margin: 0 0 0.75rem;
        font-family: var(--sf-font-sans);
        line-height: 1.3;
      }

      .product-desc {
        font-size: var(--sf-font-size-sm);
        color: var(--sf-text-secondary);
        line-height: 1.6;
        margin: 0 0 1.25rem;
        flex: 1;
      }

      .product-specs {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
        margin-bottom: 1.25rem;
      }

      .spec-tag {
        background: var(--sf-bg-secondary);
        border: 1px solid var(--sf-border-light);
        border-radius: var(--sf-radius-sm);
        padding: 3px 10px;
        font-size: var(--sf-font-size-xs);
        color: var(--sf-text-secondary);
        font-weight: 500;
      }

      .product-link {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-size: var(--sf-font-size-sm);
        font-weight: 600;
        color: var(--sf-navy);
        text-decoration: none;
        margin-top: auto;
        transition: gap 0.2s ease;
      }

      .product-card:hover .product-link {
        gap: 10px;
      }

      .section-cta {
        text-align: center;
        margin-top: 3rem;
      }
    `,
  ],
  template(this: FeaturedProductsComponent) {
    return html`
      <section class="section" id="featured-products-component">
        <div class="container">
          <div class="section-header">
            <div class="section-badge">Топ оферти</div>
            <h2 class="section-title">Препоръчани Машини</h2>
            <p class="section-subtitle">Подбрани от нашите специалисти машини с отлично съотношение качество/цена.</p>
          </div>

          <div class="grid">
            ${this.products.map(
              (product) => html`
                <div class="product-card" @click=${() => this.goto(product.categorySlug, product.slug)}>
                  <div class="product-image">
                    ${product.images[0]
                      ? html`<img class="product-image" src=${product.images[0]?.url} />`
                      : this.getCategoryIcon(product.categorySlug)}
                    <div class="product-badge">Препоръчано</div>
                  </div>

                  <div class="product-body">
                    <div class="product-category">${this.getCategoryName(product.categorySlug)}</div>
                    <h3 class="product-name">${product.name}</h3>
                    <p class="product-desc">${product.shortDescription}</p>

                    ${product.specifications.length > 0
                      ? html`
                          <div class="product-specs">
                            ${product.specifications
                              .slice(0, 3)
                              .map((spec) => html` <span class="spec-tag">${spec.key}: ${spec.value}</span> `)}
                          </div>
                        `
                      : ''}

                    <span class="product-link">Виж детайли →</span>
                  </div>
                </div>
              `
            )}
          </div>

          <div class="section-cta">
            <button class="btn btn-outline btn-lg" @click=${() => this.goto('')}>Вижте всички продукти →</button>
          </div>
        </div>
      </section>
    `;
  },
})
export class FeaturedProductsComponent extends LitElement {
  @state() products: Product[] = [];
  @state() categoryTree: Category[] = [];

  @Router() private router: Router;
  @Inject(ProductService) private productService: ProductService;
  @Inject(CategoryService) private categoryService: CategoryService;

  OnUpdateFirst() {
    this.productService.getFeaturedProducts().subscribe((p) => {
      this.products = p;
    });
    this.categoryService.getCategoryTree().subscribe((tree) => {
      this.categoryTree = tree;
    });
  }

  getCategoryIcon(slug: string): string {
    const icons: Record<string, string> = {
      'cnc-frezovi': '⚙️',
      strugove: '🔩',
      shlifovachni: '🛠️',
      probivni: '🔧',
      zavarachno: '🔥',
      specializirano: '🏭',
    };
    return icons[slug] ?? '🏭';
  }

  getCategoryName(slug: string): string {
    const names: Record<string, string> = {
      'cnc-frezovi': 'CNC Фрезови',
      strugove: 'Стругове',
      shlifovachni: 'Шлифовъчни',
      probivni: 'Пробивни',
      zavarachno: 'Заваряване',
      specializirano: 'Специализирани',
    };
    return names[slug] ?? '';
  }

  private goto(categorySlug: string, productSlug?: string | null) {
    if (productSlug) {
      // Find the product's category in the tree to build the full ancestor path
      const catNode = ProductsListComponent.findNodeBySlug(categorySlug, this.categoryTree);

      let segments: string[];

      if (catNode) {
        const ancestorPath = ProductsListComponent.buildAncestorPath(catNode, this.categoryTree);
        segments = ancestorPath
          .replace(/^\/products\/?/, '')
          .split('/')
          .filter(Boolean);
      } else {
        segments = [categorySlug];
      }

      // Ensure exactly 3 category segments (group/brand/subtype) by repeating the last one
      while (segments.length < 3) {
        segments.push(segments[segments.length - 1]);
      }

      this.router.go(`/products/${segments.join('/')}/${productSlug}`);
    } else if (categorySlug) {
      this.router.go(`/products/${categorySlug}`);
    } else {
      this.router.go(`/products`);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}
